import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquarePlus, X, ChevronLeft, Star } from 'lucide-react';
import moment from 'moment';

import { useAuth } from '../../../../context/AuthContext';
import { toast } from '../../common/Toast/toast';
import PopupDone, { usePopupDone } from '../../common/PopupDone/PopupDone';
import {
  getPendingFeedback,
  createStudentFeedback,
} from '../../../../services/StudentFeedbackService';
import { subscribeToNotifications } from '../../NotificationBell/notificationPulse';
import './feedback-widget.css';

/**
 * The feedback widget — bottom right, students only.
 *
 * It answers one question: which class have you sat in and not told us
 * about yet. The list comes from /studentfeedbacks/pending — EVERY
 * session this student was marked present for and has not yet rated,
 * whenever the batch ran. It used to be scoped to currently-running
 * batches, which meant a session you never rated dropped off the list
 * the day its batch ended, while the attendance notification announcing
 * it had already arrived.
 *
 * It refreshes on the notification pulse rather than on a clock of its
 * own: the bell already polls, and two pollers would mean the widget
 * catching up a minute after the notification that announced the
 * session. See NotificationBell/notificationPulse.js.
 */

const MAX_STARS = 5;

/* A rating is a number the reader is being asked to SET, so it is a row
   of radios rather than five buttons with click handlers: arrow keys
   work, the label is read out, and the group has one value. */
const Stars = ({ name, value, onChange, label, size = 'md' }) => (
  <div className={`fbw-stars fbw-stars--${size}`} role="radiogroup" aria-label={label}>
    {Array.from({ length: MAX_STARS }, (_, i) => i + 1).map((n) => (
      <label
        key={n}
        className={`fbw-star${n <= value ? ' is-on' : ''}`}
        title={`${n} of ${MAX_STARS}`}
      >
        <input
          type="radio"
          name={name}
          value={n}
          checked={value === n}
          onChange={() => onChange(n)}
        />
        <Star size={size === 'sm' ? 15 : 19} aria-hidden="true" />
        <span className="fbw-sr">{`${n} of ${MAX_STARS}`}</span>
      </label>
    ))}
  </div>
);

const FeedbackWidget = () => {
  const { user, hasPermission } = useAuth();

  /* Students only — and "student" is what is LEFT once admin and trainer
     are ruled out, not a permission on its own.
     
     This was `hasPermission('student-dashboard', 'view', 'lms')` and
     nothing else, which an administrator also holds: that permission is
     how staff read the staff view OF a student's dashboard. So every
     admin got a "Rate your classes" button for classes they do not sit
     in.
     
     The ladder below is AdminCalendar's, line for line (~252): rule out
     admin, then trainer, and only then ask whether the student
     permission is present. A permission answers "may you open this
     page"; it was never going to answer "are you a student". */
  const isSuperAdmin = user?.isSuperAdmin === true || user?.isSuperAdmin === 'true';
  const isAdmin = Boolean(isSuperAdmin || hasPermission('admin-calendar', 'view', 'lms'));
  const isTrainer = Boolean(
    !isAdmin
      && (hasPermission('trainer-calendar', 'view', 'lms')
        || hasPermission('trainer-calender', 'view', 'lms')),
  );
  const isStudent = Boolean(
    !isAdmin && !isTrainer && hasPermission('student-dashboard', 'view', 'lms'),
  );

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);

  // The form, per session: a rating and a note for each topic, plus one
  // rating for the trainer and one closing comment.
  const [topicForm, setTopicForm] = useState({});
  const [trainerRating, setTrainerRating] = useState(0);
  const [comment, setComment] = useState('');

  /* A sent rating is confirmed inside the panel (PopupDone), not by a
     toast. Only while a session's form is up: the panel is non-modal, so
     the list view must not take toasts raised by the page behind it. */
  const { done } = usePopupDone(open && Boolean(active));
  const closeDone = () => {
    setActive(null);
    setOpen(false);
  };

  const loadingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!isStudent || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      setPending(await getPendingFeedback(20));
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [isStudent]);

  // First load.
  useEffect(() => {
    if (!isStudent) return;
    refresh();
  }, [isStudent, refresh]);

  /* Every notification the bell reports re-reads the list. Attendance is
     the one that adds to it, but the count endpoint cannot say what
     arrived, so an unknown pulse re-reads too — one cheap request
     against the alternative of missing the session entirely. */
  useEffect(() => {
    if (!isStudent) return undefined;
    return subscribeToNotifications((pulse) => {
      // 'removed': a session deleted takes its rating card with it.
      if (pulse.type === 'attendance_marked' || pulse.type === 'removed' || pulse.type === 'unknown') refresh();
    });
  }, [isStudent, refresh]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Escape steps back through the panel rather than closing it from
      // inside a half-filled form.
      if (active) setActive(null);
      else setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, active]);

  const startSession = (session) => {
    setActive(session);
    setTopicForm(
      Object.fromEntries(
        (session.topics || []).map((topic) => [topic, { rating: 0, comment: '' }]),
      ),
    );
    setTrainerRating(0);
    setComment('');
  };

  const setTopic = (topic, patch) =>
    setTopicForm((prev) => ({ ...prev, [topic]: { ...prev[topic], ...patch } }));

  /* EVERY topic gets a star. The list is what the trainer recorded as
     covered in that session, and the point of asking per topic is that
     "the class was a 3" does not say which part of it was. A partial
     answer would land as a row that looks complete and is not, so the
     form holds until each one has a verdict.

     The star is the required part; the note beside it is not -- being
     made to write something about every topic is how a form stops
     getting filled in at all. */
  const topicList = active?.topics || [];
  /* Where the list came from decides how hard the form pushes.
     'session'  -- the trainer ticked these as covered, so each needs a
                   verdict; that is the whole point of asking per topic.
     'syllabus' -- inferred from the batch's course because the record
                   named none. Offered, never demanded: nobody said these
                   were taught that day. */
  const topicsFrom = active?.topicsFrom || (topicList.length ? 'session' : 'none');
  /* 'session' and 'legacy' both come OUT of the attendance record -- the
     trainer said these were covered, so each needs a verdict. Only
     'syllabus' is inferred, and inferred topics are offered rather than
     demanded: nobody said they were taught that day. */
  const topicsRequired = topicsFrom === 'session' || topicsFrom === 'legacy';
  const rated = topicList.filter((topic) => (topicForm[topic]?.rating || 0) > 0);
  const allTopicsRated = topicList.length > 0 && rated.length === topicList.length;

  /* A session with no topics recorded has nothing to rate one by one --
     the trainer left the field empty when marking it. That verdict still
     counts, so the trainer rating carries it on its own rather than
     leaving the student with a form they cannot submit. */
  const canSubmit = topicsRequired
    ? allTopicsRated
    : rated.length > 0 || trainerRating > 0;

  const submit = async () => {
    if (!active || !canSubmit || saving) return;
    setSaving(true);
    try {
      /* Built from the session's own topic list, in the order it was
         recorded -- not from Object.entries of the form state, whose
         order is the insertion order of a map and not something to
         depend on for what a trainer will read back. */
      const topicRatings = topicList
        .filter((topic) => (topicForm[topic]?.rating || 0) > 0)
        .map((topic) => ({
          topic,
          rating: topicForm[topic].rating,
          comment: (topicForm[topic].comment || '').trim() || undefined,
        }));

      await createStudentFeedback({
        attendanceId: active.attendanceId,
        // The session rating is left out on purpose: the API derives it
        // from the mean of the topics, which is the same number and one
        // fewer thing to ask. When only the trainer was rated there are
        // no topics to average, so it is sent explicitly.
        ...(topicRatings.length ? { topicRatings } : { rating: trainerRating }),
        ...(trainerRating ? { trainerRating } : {}),
        topics: topicRatings.map((t) => t.topic),
        comment: comment.trim() || undefined,
      });

      // The form stays up (behind the done panel) until that panel closes.
      toast.success('Thanks — your feedback has been sent.');
      // Drop it locally rather than waiting for the round trip: the row
      // is gone from /pending the moment it is written.
      setPending((prev) => prev.filter((p) => p.attendanceId !== active.attendanceId));
    } catch (error) {
      toast.error(
        error?.response?.data?.message?.message
          || error?.response?.data?.message
          || 'That could not be sent. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isStudent || !user) return null;

  const count = pending.length;

  return createPortal(
    <div className="fbw-root">
      {open && (
        <section
          className="fbw-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="fbw-title"
        >
          <header className="fbw-head">
            {active ? (
              <button
                type="button"
                className="fbw-back"
                onClick={() => setActive(null)}
                aria-label="Back to the list"
              >
                <ChevronLeft size={17} aria-hidden="true" />
              </button>
            ) : (
              <span className="fbw-head-mark" aria-hidden="true">
                <MessageSquarePlus size={16} />
              </span>
            )}

            <div className="fbw-head-id">
              <h2 className="fbw-title" id="fbw-title">
                {active ? 'How was this class?' : 'Rate your classes'}
              </h2>
              <p className="fbw-sub">
                {active
                  ? `${active.batchName || 'Your batch'} · ${moment(active.date).format('ddd D MMM')}`
                  : count === 0
                    ? 'Nothing waiting'
                    : `${count} ${count === 1 ? 'class is' : 'classes are'} waiting`}
              </p>
            </div>

            <button
              type="button"
              className="fbw-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </header>

          <div className="fbw-body">
            {/* ── One session's form ─────────────────────────────── */}
            {done ? (
              <PopupDone message={done} onClose={closeDone} />
            ) : active ? (
              <>
                {topicList.length === 0 ? (
                  <p className="fbw-note">
                    No topics were recorded for this class and its course has no
                    syllabus yet, so there is nothing to rate one by one. Rate the
                    trainer below instead.
                  </p>
                ) : (
                  <>
                    <div className="fbw-topics-head">
                      <span className="fbw-label">
                        {topicsRequired ? 'What was covered' : 'From your syllabus'}
                        <span className="fbw-req" aria-hidden="true">
                          {topicsRequired ? 'required' : 'optional'}
                        </span>
                      </span>
                      <span className="fbw-progress">
                        {rated.length}/{topicList.length}
                      </span>
                    </div>
                    <ul className="fbw-topics">
                    {topicList.map((topic) => (
                      <li
                        className={`fbw-topic${
                          (topicForm[topic]?.rating || 0) > 0 ? ' is-rated' : ''
                        }`}
                        key={topic}
                      >
                        <div className="fbw-topic-head">
                          <span className="fbw-topic-name">{topic}</span>
                          <Stars
                            size="sm"
                            name={`topic-${active.attendanceId}-${topic}`}
                            label={`Rating for ${topic}`}
                            value={topicForm[topic]?.rating || 0}
                            onChange={(n) => setTopic(topic, { rating: n })}
                          />
                        </div>
                        {/* The note appears once the topic has a rating:
                            an empty box under every topic reads as six
                            things to fill in rather than six to judge. */}
                        {topicForm[topic]?.rating > 0 && (
                          <input
                            type="text"
                            className="fbw-input"
                            placeholder="Anything to add about this topic? (optional)"
                            maxLength={1000}
                            value={topicForm[topic]?.comment || ''}
                            onChange={(e) => setTopic(topic, { comment: e.target.value })}
                          />
                        )}
                      </li>
                    ))}
                    </ul>
                  </>
                )}

                <div className="fbw-field">
                  <span className="fbw-label">The trainer</span>
                  <Stars
                    name={`trainer-${active.attendanceId}`}
                    label="Rating for the trainer"
                    value={trainerRating}
                    onChange={setTrainerRating}
                  />
                  <p className="fbw-hint">
                    How it was taught, as distinct from what was covered.
                  </p>
                </div>

                <div className="fbw-field">
                  <label className="fbw-label" htmlFor="fbw-comment">
                    Anything else
                  </label>
                  <textarea
                    id="fbw-comment"
                    className="fbw-input fbw-textarea"
                    rows={3}
                    maxLength={2000}
                    placeholder="Optional"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>
              </>
            ) : loading && count === 0 ? (
              <p className="fbw-note">Looking for classes to rate…</p>
            ) : count === 0 ? (
              <div className="fbw-empty">
                <span className="fbw-empty-mark" aria-hidden="true">
                  <MessageSquarePlus size={20} />
                </span>
                <p className="fbw-empty-title">You are all caught up</p>
                <p className="fbw-note">
                  When a class is marked, it appears here for you to rate.
                </p>
              </div>
            ) : (
              /* ── The waiting list ──────────────────────────────── */
              <ul className="fbw-list">
                {pending.map((session) => (
                  <li key={session.attendanceId}>
                    <button
                      type="button"
                      className="fbw-row"
                      onClick={() => startSession(session)}
                    >
                      <span className="fbw-row-main">
                        <span className="fbw-row-name">
                          {session.batchName || 'Your batch'}
                        </span>
                        <span className="fbw-row-sub">
                          {moment(session.date).format('ddd D MMM')}
                          {session.topics?.length
                            ? ` · ${session.topics.length} ${
                                session.topics.length === 1 ? 'topic' : 'topics'
                              }`
                            : ''}
                        </span>
                      </span>
                      <span className="fbw-row-go" aria-hidden="true">
                        Rate
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {active && !done && (
            <footer className="fbw-foot">
              <span className="fbw-tally">
                {canSubmit
                  ? 'Ready to send'
                  : topicsRequired
                    ? `${topicList.length - rated.length} topic${
                        topicList.length - rated.length === 1 ? '' : 's'
                      } left to rate`
                    : topicList.length
                      ? 'Rate a topic, or the trainer'
                      : 'Rate the trainer to send'}
              </span>
              <button
                type="button"
                className="fbw-send"
                disabled={!canSubmit || saving}
                onClick={submit}
              >
                {saving ? 'Sending…' : 'Send feedback'}
              </button>
            </footer>
          )}
        </section>
      )}

      {/* The launcher. It carries the count, because the whole reason to
          look at it is that something is waiting. */}
      <button
        type="button"
        className={`fbw-fab${open ? ' is-open' : ''}`}
        onClick={() => {
          setOpen((v) => !v);
          setActive(null);
          if (!open) refresh();
        }}
        aria-expanded={open}
        aria-label={
          count > 0
            ? `Rate your classes, ${count} waiting`
            : 'Rate your classes'
        }
      >
        {open ? <X size={20} aria-hidden="true" /> : <MessageSquarePlus size={20} aria-hidden="true" />}
        {!open && count > 0 && <span className="fbw-fab-count">{count > 9 ? '9+' : count}</span>}
      </button>
    </div>,
    document.body,
  );
};

export default FeedbackWidget;
